import { useState, useEffect } from 'react';
import api from '../../config/axios';
import { MdAdd, MdEdit, MdDelete, MdClose, MdSearch, MdFilterList } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const ProjectSettings = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState({
    completed: false,
    inProgress: false,
    planning: false,
    cancelled: false
  });
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [formData, setFormData] = useState({
    project_name: '',
    project_description: '',
    project_start_date: '',
    project_end_date: '',
    project_vendor: '',
    vendor_pm: '',
    project_hours: '',
    project_status: 'planning',
    created_by: '',
    assigned_from: ''
  });

  // Kullanıcı bilgilerini al
  const [userInfo, setUserInfo] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'confirm',
    onConfirm: null,
    showCancel: true
  });

  useEffect(() => {
    fetchUserInfo();
    fetchProjects();
  }, []);

  // Status filter dropdown'ını dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStatusFilter && !event.target.closest('.status-filter-container')) {
        setShowStatusFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusFilter]);

  useEffect(() => {
    if (showForm) {
      const timer = setTimeout(() => {
        const input = document.querySelector('input[name="project_name"]');
        if (input) input.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showForm]);


  // Kullanıcı bilgilerini çek (Header'daki gibi localStorage'dan)
  const fetchUserInfo = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        const fullName = userData.fullName || '';
        setUserInfo({ name: fullName });
        setFormData(prev => ({
          ...prev,
          created_by: fullName
        }));
      }
    } catch (error) {
      console.error('Kullanıcı bilgileri alınırken hata:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      if (response.data.success) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error('Projeler yüklenirken hata:', error);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin');
      } else {
        // Tablo yoksa boş array set et
        setProjects([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (project, projectName) => {
    const projectIdentifier = project.project_uuid || project.id; // UUID varsa UUID, yoksa ID kullan
    
    showPopup({
      title: t('projectSettings.deleteProject'),
      message: t('projectSettings.deleteConfirm', { name: projectName }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/projects/${projectIdentifier}`);
          if (response.data.success) {
            // Silme işlemi başarılı olduktan sonra proje listesini yeniden yükle
            fetchProjects();
            showPopup({
              title: t('common.success'),
              message: t('projectSettings.projectDeleted'),
              type: 'success',
              showCancel: false,
              onConfirm: closePopup
            });
          }
        } catch (error) {
          showPopup({
            title: t('common.error'),
            message: error.response?.data?.message || t('projectSettings.projectDeleteError'),
            type: 'error',
            showCancel: false,
            onConfirm: closePopup
          });
        }
      }
    });
  };

  const projectStatusOptions = [
    { value: 'planning', label: t('projectSettings.planning') },
    { value: 'inProgress', label: t('projectSettings.inProgress') },
    { value: 'completed', label: t('projectSettings.completed') },
    { value: 'cancelled', label: t('projectSettings.cancelled') }
  ];

  const handleEdit = (project) => {
    // Tarih formatını düzenle - form için YYYY-MM-DD formatına çevir
    const formatDateForForm = (dateString) => {
      if (!dateString || dateString === '-') return '';
      try {
        // Türkçe tarih formatından (DD.MM.YYYY) ISO formatına çevir
        if (dateString.includes('.')) {
          const [day, month, year] = dateString.split('.');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        return dateString;
      } catch (error) {
        console.error('Date format error:', error);
        return '';
      }
    };

    setFormData({
      project_name: project.project_name,
      project_description: project.project_description,
      project_start_date: formatDateForForm(project.project_start_date),
      project_end_date: formatDateForForm(project.project_end_date),
      project_vendor: project.project_vendor,
      vendor_pm: project.vendor_pm || '',
      project_hours: project.project_hours,
      project_status: project.project_status,
      created_by: project.created_by,
      assigned_from: project.assigned_from || ''
    });
    setIsEditing(true);
    setEditingId(project.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      
      // Tarih alanlarını temizle - boş string'leri null'a çevir
      const submitData = {
        ...formData,
        project_start_date: formData.project_start_date && formData.project_start_date.trim() !== '' ? formData.project_start_date : null,
        project_end_date: formData.project_end_date && formData.project_end_date.trim() !== '' ? formData.project_end_date : null
      };
      
      if (isEditing) {
        response = await api.put(`/projects/${editingId}`, submitData);
      } else {
        response = await api.post('/projects', submitData);
      }

      if (response.data.success) {
        // Form'u hemen kapat
        setShowForm(false);
        setIsEditing(false);
        setEditingId(null);
        
        // Proje listesini güncelle
        fetchProjects();
        
        // Form verilerini sıfırla (created_by'ı koru)
        setFormData(prev => ({
          project_name: '',
          project_description: '',
          project_start_date: '',
          project_end_date: '',
          project_vendor: '',
          vendor_pm: '',
          project_hours: '',
          project_status: 'planning',
          created_by: prev.created_by,
          assigned_from: ''
        }));
        
        // Popup'ı göster
        showPopup({
          title: t('common.success'),
          message: isEditing 
            ? t('projectSettings.projectUpdated')
            : t('projectSettings.projectCreated'),
          type: 'success',
          showCancel: false,
          onConfirm: closePopup
        });
      }
    } catch (error) {
      showPopup({
        title: t('common.error'),
        message: error.response?.data?.message || t('projectSettings.operationError'),
        type: 'error',
        showCancel: false,
        onConfirm: closePopup
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Popup yönetimi için fonksiyonlar
  const showPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || 'Onay',
      message: options.message,
      type: options.type || 'confirm',
      onConfirm: options.onConfirm,
      showCancel: options.showCancel ?? true
    });
  };

  const closePopup = () => {
    setPopup(prev => ({ ...prev, show: false }));
  };

  // Sayfa boyutu seçenekleri
  const pageSizeOptions = [10, 20, 50, 100, 500];

  // Sayfa boyutunu değiştirme fonksiyonu
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Sayfa boyutu değişince ilk sayfaya dön
  };

  // Filtrelenmiş projeler
  const filteredProjects = projects.filter(project => {
    // Search term filtrelemesi
    const matchesSearch = searchTerm === '' || 
      (project.project_name && project.project_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.project_description && project.project_description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.project_vendor && project.project_vendor.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.created_by && project.created_by.toLowerCase().includes(searchTerm.toLowerCase()));

    if (!matchesSearch) return false;

    // Status filtrelemesi
    const selectedStatuses = Object.keys(statusFilter).filter(status => statusFilter[status]);
    if (selectedStatuses.length === 0) return true; // Hiç status seçilmemişse tümünü göster

    return selectedStatuses.includes(project.project_status);
  });

  // Sayfalama hesaplamaları
  const indexOfLastProject = currentPage * itemsPerPage;
  const indexOfFirstProject = indexOfLastProject - itemsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300';
      case 'inProgress':
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300';
      case 'completed':
        return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300';
      case 'cancelled':
        return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300';
      default:
        return 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    const statusOption = projectStatusOptions.find(option => option.value === status);
    return statusOption ? statusOption.label : status;
  };

  // Filtreleme fonksiyonları
  const handleStatusFilterChange = (status) => {
    setStatusFilter(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter({
      completed: false,
      inProgress: false,
      planning: false,
      cancelled: false
    });
  };

  return (
    <div className="h-full overflow-auto">
      <div className="flex justify-between items-center gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-medium text-gray-800 dark:text-white">{t('projectSettings.projectManagement')}</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] transition-colors w-11 h-10 sm:w-auto sm:h-auto sm:px-6 sm:py-3 -ml-2.5 sm:ml-0"
        >
          <MdAdd size={20} className="sm:hidden" />
          <MdAdd size={24} className="hidden sm:block" />
          <span className="hidden sm:inline">{t('projectSettings.addNewProject')}</span>
        </button>
      </div>

      {/* Total Count */}
      <div className="mb-4">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {t('projectSettings.totalProjects', { count: filteredProjects.length })}
        </span>
      </div>

      {/* Project Listesi */}
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${totalPages <= 1 ? 'rounded-b-lg' : ''}`}>
        {/* Search ve Filter */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-[#565c66] bg-gray-50 dark:bg-gray-700 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between rounded-tl-lg rounded-tr-lg">
          {/* Search ve Status Filter */}
          <div className="flex items-center gap-3 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Proje ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <MdSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-gray-500" size={20} />
            </div>

            {/* Status Filter */}
            <div className="relative status-filter-container">
              <button
                onClick={() => setShowStatusFilter(!showStatusFilter)}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
              >
                <MdFilterList size={16} />
                Status
                {Object.values(statusFilter).some(Boolean) && (
                  <span className="bg-blue-500 text-white text-xs rounded-full px-1.5 py-0.5">
                    {filteredProjects.filter(project => {
                      const selectedStatuses = Object.keys(statusFilter).filter(status => statusFilter[status]);
                      return selectedStatuses.includes(project.project_status);
                    }).length}
                  </span>
                )}
              </button>

              {/* Status Filter Dropdown */}
              {showStatusFilter && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50">
                  <div className="p-2">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Status Seçin</div>
                    {[
                      { key: 'completed', label: 'Completed' },
                      { key: 'inProgress', label: 'In Progress' },
                      { key: 'planning', label: 'Planning' },
                      { key: 'cancelled', label: 'Cancelled' }
                    ].map(status => (
                      <label key={status.key} className="flex items-center gap-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={statusFilter[status.key]}
                          onChange={() => handleStatusFilterChange(status.key)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{status.label}</span>
                      </label>
                    ))}
                    <div className="border-t border-gray-200 dark:border-gray-600 mt-2 pt-2">
                      <button
                        onClick={clearAllFilters}
                        className="w-full text-left px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        Tüm Filtreleri Temizle
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sayfa boyutu seçici */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('common.show')}</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('table.entries')}</span>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          {currentProjects.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500 dark:text-gray-400 mb-2">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Henüz hiç proje bulunmuyor
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Proje eklemek için yukarıdaki 'Yeni Proje Ekle' butonunu kullanın.
              </p>
            </div>
          ) : (
            <table className="w-full min-w-[600px] table-auto">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
              <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('projectSettings.projectName')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('projectSettings.projectStatus')}</th>
                 <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('projectSettings.projectStartDate')}</th>
                 <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('projectSettings.projectEndDate')}</th>
                 <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('projectSettings.projectVendor')}</th>
                 <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">Created By</th>
                 <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('projectDetail.assignedFrom')}</th>
                <th className="px-4 py-2 text-right text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('table.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {currentProjects.map((project, index) => {
                const isLast = index === currentProjects.length - 1 && totalPages <= 1;
                return (
                   <tr 
                     key={project.id} 
                     className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200"
                     onClick={() => navigate(`/projects/${project.project_uuid || project.id}`)}
                   >
                    <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>
                      {project.project_name}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${getStatusColor(project.project_status)}`}>
                        {getStatusLabel(project.project_status)}
                      </span>
                    </td>
                     <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{project.project_start_date || '-'}</td>
                     <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{project.project_end_date || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{project.project_vendor}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{project.created_by}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{project.assigned_from || '-'}</td>
                     <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right font-medium ${isLast ? 'rounded-br-lg' : ''}`}>
                       <button
                         onClick={(e) => {
                           e.stopPropagation();
                           handleEdit(project);
                         }}
                         className="text-indigo-600 hover:text-indigo-900 mr-3"
                       >
                         <MdEdit size={18} />
                       </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(project, project.project_name);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        <MdDelete size={18} />
                      </button>
                    </td>
                  </tr>
                );
               })}
             </tbody>
           </table>
           )}
         </div>

        {/* Mobile Card View */}
        <div className="lg:hidden">
          {currentProjects.length === 0 ? (
            <div className="text-center py-12 px-4">
              <div className="text-gray-500 dark:text-gray-400 mb-2">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Henüz hiç proje bulunmuyor
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Proje eklemek için yukarıdaki 'Yeni Proje Ekle' butonunu kullanın.
              </p>
            </div>
          ) : (
            <div className="space-y-4 p-4">
              {currentProjects.map((project, index) => (
               <div 
                 key={project.id}
                 className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                 onClick={() => navigate(`/projects/${project.project_uuid || project.id}`)}
               >
                {/* Project Name & Status */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectSettings.projectName')}</div>
                    <div className="text-sm text-gray-900 dark:text-white font-medium">{project.project_name}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectSettings.projectStatus')}</div>
                    <div>
                      <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${getStatusColor(project.project_status)}`}>
                        {getStatusLabel(project.project_status)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Start Date & End Date */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                   <div>
                     <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectSettings.projectStartDate')}</div>
                     <div className="text-sm text-gray-900 dark:text-white">{project.project_start_date || '-'}</div>
                   </div>
                   <div>
                     <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectSettings.projectEndDate')}</div>
                     <div className="text-sm text-gray-900 dark:text-white">{project.project_end_date || '-'}</div>
                   </div>
                </div>

                {/* Vendor & Created By */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectSettings.projectVendor')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{project.project_vendor}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Created By</div>
                    <div className="text-sm text-gray-900 dark:text-white">{project.created_by}</div>
                  </div>
                </div>

                {/* Assigned From */}
                <div className="mb-3">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.assignedFrom')}</div>
                  <div className="text-sm text-gray-900 dark:text-white">{project.assigned_from || '-'}</div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-2">
                   <button
                     onClick={(e) => {
                       e.stopPropagation();
                       handleEdit(project);
                     }}
                     className="text-indigo-600 hover:text-indigo-900 p-1"
                   >
                     <MdEdit size={16} />
                   </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(project, project.project_name);
                    }}
                    className="text-red-600 hover:text-red-900 p-1"
                  >
                    <MdDelete size={16} />
                  </button>
                </div>
               </div>
             ))}
           </div>
           )}
         </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-b-lg">
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('table.showing')}{' '}
                  <span className="font-medium">{indexOfFirstProject + 1}</span>
                  {' '}-{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastProject, projects.length)}
                  </span>{' '}
                  {t('table.of')}{' '}
                  <span className="font-medium">{projects.length}</span>{' '}
                  {t('projectSettings.projects')}
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('table.previous')}
                  </button>
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => handlePageChange(index + 1)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                        currentPage === index + 1
                          ? 'bg-[#272c39] text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('table.next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Popup */}
      {popup.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">{popup.title}</h3>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <div className="text-center">
                <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                  {popup.message}
                </p>
                <div className="flex justify-center gap-4">
                  {popup.showCancel && (
                    <button
                      onClick={closePopup}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      {t('common.cancel')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (popup.onConfirm) {
                        popup.onConfirm();
                      } else {
                        closePopup();
                      }
                    }}
                    className={`px-4 py-2 text-white rounded-md ${
                      popup.type === 'error' 
                        ? 'bg-red-600 hover:bg-red-700'
                        : popup.type === 'success'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-[#272c39] hover:bg-[#1e2230]'
                    }`}
                  >
                    {popup.type === 'confirm' ? t('common.confirm') : t('common.ok')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Project Ekleme/Düzenleme Modal Formu */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center p-2 sm:p-4">
          <div className="relative mx-auto w-full max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
              <h3 className="text-sm sm:text-lg font-medium">
                {isEditing ? t('projectSettings.editProject') : t('projectSettings.addNewProject')}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setIsEditing(false);
                  setEditingId(null);
                  setFormData(prev => ({
                    project_name: '',
                    project_description: '',
                    project_start_date: '',
                    project_end_date: '',
                    project_vendor: '',
                    vendor_pm: '',
                    project_hours: '',
                    project_status: 'planning',
                    created_by: prev.created_by,
                    assigned_from: ''
                  }));
                }}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={20} />
              </button>
            </div>
            
            <div className="p-3 sm:p-8 bg-white dark:bg-gray-800">
               <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                 {/* Project Name */}
                 <div className="col-span-1 sm:col-span-2">
                   <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('projectSettings.projectName')} <span className="text-red-500">*</span>
                   </label>
                   <input
                     type="text"
                     name="project_name"
                     value={formData.project_name}
                     onChange={handleChange}
                     className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                     required
                   />
                 </div>

                 {/* Status, Vendor, Vendor PM - 3 sütun yan yana */}
                 <div className="col-span-1 sm:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                   {/* Status */}
                   <div>
                     <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                       {t('projectSettings.projectStatus')}
                     </label>
                     <select
                       name="project_status"
                       value={formData.project_status}
                       onChange={handleChange}
                       className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                     >
                       {projectStatusOptions.map(option => (
                         <option key={option.value} value={option.value}>{option.label}</option>
                       ))}
                     </select>
                   </div>

                   {/* Vendor */}
                   <div>
                     <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                       {t('projectSettings.projectVendor')}
                     </label>
                     <input
                       type="text"
                       name="project_vendor"
                       value={formData.project_vendor}
                       onChange={handleChange}
                       className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                     />
                   </div>

                   {/* Vendor PM */}
                   <div>
                     <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                       Vendor PM
                     </label>
                     <input
                       type="text"
                       name="vendor_pm"
                       value={formData.vendor_pm}
                       onChange={handleChange}
                       className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                     />
                   </div>
                 </div>

                 {/* Start Date */}
                 <div>
                   <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('projectSettings.projectStartDate')}
                   </label>
                   <input
                     type="date"
                     name="project_start_date"
                     value={formData.project_start_date}
                     onChange={handleChange}
                     className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                   />
                 </div>

                 {/* End Date */}
                 <div>
                   <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('projectSettings.projectEndDate')}
                   </label>
                   <input
                     type="date"
                     name="project_end_date"
                     value={formData.project_end_date}
                     onChange={handleChange}
                     className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                   />
                 </div>

                 {/* Project Description */}
                 <div className="col-span-1 sm:col-span-2">
                   <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('projectSettings.projectDescription')}
                   </label>
                   <textarea
                     name="project_description"
                     value={formData.project_description}
                     onChange={handleChange}
                     rows={3}
                     className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                   />
                 </div>

                 {/* Hours */}
                 <div className="col-span-1 sm:col-span-2">
                   <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('projectSettings.projectHours')}
                   </label>
                   <textarea
                     name="project_hours"
                     value={formData.project_hours}
                     onChange={handleChange}
                     rows={3}
                     className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                     placeholder="Proje saatleri hakkında detaylı bilgi giriniz"
                   />
                 </div>

                 {/* Members */}

                 {/* Assigned From */}
                 <div className="col-span-1 sm:col-span-2">
                   <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {t('projectDetail.assignedFrom')}
                   </label>
                   <input
                     type="text"
                     name="assigned_from"
                     value={formData.assigned_from}
                     onChange={handleChange}
                     className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                     placeholder="Projeyi atayan kişinin adı soyadı"
                   />
                 </div>

                {/* Buttons */}
                <div className="col-span-1 sm:col-span-2 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-3 sm:mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setIsEditing(false);
                      setEditingId(null);
                    }}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 text-sm sm:text-base"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] text-sm sm:text-base"
                  >
                    {isEditing ? t('projectSettings.updateProject') : t('projectSettings.addProject')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSettings;
